/* ──────────────────────────────────────────────────────────────
   src/components/ReunionReglaFormDialog.tsx
   Diálogo para crear / editar reglas de agendación
   ────────────────────────────────────────────────────────────── */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
  Autocomplete,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  REUNION_TIPO_VALUES,
  REUNION_ROL_DESTINO_VALUES,
  REUNION_CANAL_VALUES,
} from '../../api/reunionReglas.api';
import type { ReunionReglaFormValues } from '../../hooks/useReunionReglas';
import { useUsuarios } from '../../hooks/useUsuarios';
import { useCentros } from '../../hooks/useCentros';

export interface ReunionReglaFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  initialValues: ReunionReglaFormValues;
  onClose: () => void;
  onSubmit: (values: ReunionReglaFormValues) => Promise<void> | void;
}

const numberFieldSx = {
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': { borderColor: '#21a598 !important' },
    '&:hover fieldset': { borderColor: '#21a598 !important' },
  },
  '& .MuiInputLabel-root': {
    '&.Mui-focused': { color: '#21a598 !important' },
  },
} as const;

export default function ReunionReglaFormDialog({
  open,
  mode,
  initialValues,
  onClose,
  onSubmit,
}: ReunionReglaFormDialogProps) {
  const { t } = useTranslation<'translation'>();
  const [values, setValues] = useState<ReunionReglaFormValues>(initialValues);
  const [submitting, setSubmitting] = useState(false);
  
  // Cargar usuarios y centros para los selects
  const { rows: usuarios = [] } = useUsuarios({ soloActivos: true, sinClientes: true });
  const { rows: centros = [] } = useCentros();
  
  // Filtrar usuarios por rol de staff (excluir profesores, consultores, clientes)
  const staffUsuarios = useMemo(() => {
    return usuarios.filter((u: any) => {
      const rol = String(u.rol ?? '').toLowerCase();
      return rol !== 'profesor' && rol !== 'consultor' && rol !== 'cliente';
    });
  }, [usuarios]);

  useEffect(() => {
    if (open) {
      setValues(initialValues);
    }
  }, [open, initialValues]);

  const title = useMemo(
    () =>
      mode === 'create'
        ? t('reunionRules.dialog.new', 'Nueva regla de reunión')
        : t('reunionRules.dialog.edit', 'Editar regla de reunión'),
    [mode, t],
  );

  const handleChange = (field: keyof ReunionReglaFormValues, value: any) => {
    setValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNumberChange = (field: keyof ReunionReglaFormValues) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (raw === '') {
        handleChange(field, null);
      } else {
        const n = Number(raw);
        handleChange(field, Number.isFinite(n) ? n : null);
      }
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!values.tipoReunion || !values.rolStaffDestino) return;
    try {
      setSubmitting(true);
      await onSubmit(values);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const errorRange =
    values.diasDesdeEventoMin != null &&
    values.diasDesdeEventoMax != null &&
    Number(values.diasDesdeEventoMin) > Number(values.diasDesdeEventoMax);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Nombre */}
            <TextField
              fullWidth
              size="small"
              label={String(t('reunionRules.fields.name', 'Nombre descriptivo'))}
              placeholder={String(t('reunionRules.fields.namePlaceholder', 'Ej: Welcome 1, Reuniones apoyo psicologico'))}
              value={values.nombre ?? ''}
              onChange={(e) => handleChange('nombre', e.target.value || null)}
              helperText={String(t('reunionRules.fields.nameHelp', 'Nombre descriptivo para identificar esta regla'))}
            />

            {/* Tipo de reunión */}
            <TextField
              select
              fullWidth
              size="small"
              label={t('reunionRules.fields.type', 'Tipo de reunión')}
              value={values.tipoReunion}
              onChange={(e) => handleChange('tipoReunion', e.target.value)}
              required
            >
              {REUNION_TIPO_VALUES.map((v) => (
                <MenuItem key={v} value={v}>
                  {t(`reunionRules.type.${v}`, v)}
                </MenuItem>
              ))}
            </TextField>

            {/* Rol destino */}
            <TextField
              select
              fullWidth
              size="small"
              label={t('reunionRules.fields.role', 'Rol destino')}
              value={values.rolStaffDestino}
              onChange={(e) => handleChange('rolStaffDestino', e.target.value)}
              required
            >
              {REUNION_ROL_DESTINO_VALUES.map((v) => (
                <MenuItem key={v} value={v}>
                  {t(`reunionRules.role.${v}`, v)}
                </MenuItem>
              ))}
            </TextField>

            {/* Canal preferente */}
            <TextField
              select
              fullWidth
              size="small"
              label={t('reunionRules.fields.channel', 'Canal preferente')}
              value={values.canalPreferente ?? 'indistinto'}
              onChange={(e) =>
                handleChange('canalPreferente', e.target.value === '' ? null : e.target.value)
              }
            >
              {REUNION_CANAL_VALUES.map((v) => (
                <MenuItem key={v} value={v}>
                  {t(`reunionRules.channel.${v}`, v)}
                </MenuItem>
              ))}
            </TextField>

            {/* Días desde evento */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                type="number"
                size="small"
                label={t('reunionRules.fields.daysFromEventMin', 'Días desde evento (mín.)')}
                value={values.diasDesdeEventoMin ?? ''}
                onChange={handleNumberChange('diasDesdeEventoMin')}
                sx={{ ...numberFieldSx, flex: 1 }}
                inputProps={{ min: 0 }}
              />
              <TextField
                type="number"
                size="small"
                label={t('reunionRules.fields.daysFromEventMax', 'Días desde evento (máx.)')}
                value={values.diasDesdeEventoMax ?? ''}
                onChange={handleNumberChange('diasDesdeEventoMax')}
                sx={{ ...numberFieldSx, flex: 1 }}
                inputProps={{ min: 0 }}
              />
            </Box>

            {errorRange && (
              <Typography variant="caption" color="error">
                {t('reunionRules.errors.range', 'El mínimo no puede ser mayor que el máximo.')}
              </Typography>
            )}

            {/* Duración */}
            <TextField
              type="number"
              size="small"
              label={t('reunionRules.fields.duration', 'Duración estándar (minutos)')}
              value={values.duracionMinutos ?? ''}
              onChange={handleNumberChange('duracionMinutos')}
              sx={numberFieldSx}
              inputProps={{ min: 5, step: 5 }}
            />

            {/* Usuario específico */}
            <Autocomplete
              options={staffUsuarios}
              getOptionLabel={(option: any) => option.nombreCompleto || `Usuario ${option.id}`}
              value={staffUsuarios.find((u: any) => u.id === values.idUsuario) || null}
              onChange={(_, newValue: any) => handleChange('idUsuario', newValue?.id || null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={String(t('reunionRules.fields.user', 'Usuario específico (opcional)'))}
                  helperText={String(t('reunionRules.fields.userHelp', 'Si se especifica, solo este usuario usará esta regla'))}
                />
              )}
            />

            {/* Centro específico */}
            <TextField
              select
              fullWidth
              size="small"
              label={String(t('reunionRules.fields.center', 'Centro específico (opcional)'))}
              value={values.idCentro ?? ''}
              onChange={(e) => handleChange('idCentro', e.target.value ? Number(e.target.value) : null)}
              helperText={String(t('reunionRules.fields.centerHelp', 'Si se especifica, solo este centro usará esta regla'))}
            >
              <MenuItem value="">Todos los centros</MenuItem>
              {centros.map((c: any) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.nombre || c.nombre_centro || `Centro ${c.id}`}
                </MenuItem>
              ))}
            </TextField>

            {/* Es recurrente */}
            <FormControlLabel
              control={
                <Switch
                  checked={values.esRecurrente ?? false}
                  onChange={(e) => handleChange('esRecurrente', e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#21a598 !important',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#21a598 !important',
                    },
                  }}
                />
              }
              label={String(t('reunionRules.fields.recurrent', 'Regla recurrente'))}
            />

            {/* Campos condicionales para reglas recurrentes */}
            {values.esRecurrente && (
              <>
                <TextField
                  type="number"
                  size="small"
                  label={String(t('reunionRules.fields.frequencyWeeks', 'Frecuencia (semanas)'))}
                  value={values.frecuenciaSemanas ?? ''}
                  onChange={handleNumberChange('frecuenciaSemanas')}
                  sx={numberFieldSx}
                  inputProps={{ min: 1 }}
                  helperText={String(t('reunionRules.fields.frequencyWeeksHelp', 'Cada cuántas semanas se repite (ej: 1 = cada semana)'))}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.usarCanalDeClase ?? false}
                      onChange={(e) => handleChange('usarCanalDeClase', e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#21a598 !important',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#21a598 !important',
                        },
                      }}
                    />
                  }
                  label={String(t('reunionRules.fields.useClassChannel', 'Usar canal de la clase'))}
                />
              </>
            )}

            {/* Servicio ID */}
            <TextField
              type="number"
              size="small"
              label={String(t('reunionRules.fields.serviceId', 'ID de Servicio (opcional)'))}
              value={values.servicioId ?? ''}
              onChange={handleNumberChange('servicioId')}
              sx={numberFieldSx}
              inputProps={{ min: 1 }}
              helperText={String(t('reunionRules.fields.serviceIdHelp', 'Para reglas específicas por servicio (ej: 5, 6, 10)'))}
            />

            {/* Cálculo de fecha */}
            <TextField
              select
              fullWidth
              size="small"
              label={String(t('reunionRules.fields.dateCalculation', 'Cálculo de fecha (opcional)'))}
              value={values.calculoFecha ?? ''}
              onChange={(e) => handleChange('calculoFecha', e.target.value || null)}
              helperText={String(t('reunionRules.fields.dateCalculationHelp', 'Método especial para calcular la fecha'))}
            >
              <MenuItem value="">Por defecto (días desde evento)</MenuItem>
              <MenuItem value="relativo_clase">Relativo a clase</MenuItem>
              <MenuItem value="semana_siguiente">Semana siguiente</MenuItem>
              <MenuItem value="dias_desde_evento">Días desde evento</MenuItem>
            </TextField>

            {/* Minutos relativos a clase */}
            {values.calculoFecha === 'relativo_clase' && (
              <TextField
                type="number"
                size="small"
                label={String(t('reunionRules.fields.minutesFromClass', 'Minutos después de la clase'))}
                value={values.minutosRelativoClase ?? ''}
                onChange={handleNumberChange('minutosRelativoClase')}
                sx={numberFieldSx}
                inputProps={{ min: 0 }}
                helperText={String(t('reunionRules.fields.minutesFromClassHelp', 'Minutos después del fin de la clase'))}
              />
            )}

            {/* Es WC1 */}
            <FormControlLabel
              control={
                <Switch
                  checked={values.esWc1 ?? false}
                  onChange={(e) => handleChange('esWc1', e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#21a598 !important',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#21a598 !important',
                    },
                  }}
                />
              }
              label={String(t('reunionRules.fields.isWc1', 'Es regla WC1 (Welcome Call 1)'))}
            />

            {/* Es WC2 */}
            <FormControlLabel
              control={
                <Switch
                  checked={values.esWc2 ?? false}
                  onChange={(e) => handleChange('esWc2', e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#21a598 !important',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#21a598 !important',
                    },
                  }}
                />
              }
              label={String(t('reunionRules.fields.isWc2', 'Es regla WC2 (Welcome Call 2)'))}
            />

            {/* Activa */}
            <FormControlLabel
              control={
                <Switch
                  checked={values.activa}
                  onChange={(e) => handleChange('activa', e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#21a598 !important',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#21a598 !important',
                    },
                  }}
                />
              }
              label={String(t('reunionRules.fields.active', 'Regla activa'))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" disabled={submitting}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || !values.tipoReunion || !values.rolStaffDestino || errorRange}
            sx={{
              backgroundColor: '#21a598',
              '&:hover': { backgroundColor: '#1a8578' },
              boxShadow: 'none',
            }}
          >
            {mode === 'create' ? t('common.create', 'Crear') : t('common.save', 'Guardar')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
